import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getPersonalCyberQuote } from "../../../../api/quoteServices";
import tickicon from "../../../../assets/images/done.svg";
import listblackicon from "../../../../assets/images/list-black.svg";
import { getLoggedInUserDataFromLocalStorage } from "../../../../config/config";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePersonalCyberFlow from "../../../../hooks/usePersonalCyberFlow";
import usePersonalCyberFormData from "../../../../hooks/usePersonalCyberFormData";
import {
  selectLoginTokenInfo,
  setLoginTokenInfo,
} from "../../../../redux/features/loginRegisterSlice";
import { selectPersonalCyberForm } from "../../../../redux/features/personalCyberFormSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import {
  fetchPartyCode,
  fetchPersonalCyberQuote,
  selectPartyCode,
  selectPersonalCyberQuote,
} from "../../../../redux/features/quoteSlice";
import {
  formatPremiumValue,
  formatPremiumWithCurrency,
} from "../../../../utils/index";
import TravelSummaryModal from "../../../newTravelPolicy/modals/travelSummaryModal";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import PersonalCyberJourneyLayout from "../../layouts/PersonalCyberJourneyLayout";
import PersonalCyberBenefitsModal from "../../modals/personalCyberBenefitsModal";
import PersonalCyberLoginPromptModal from "../../modals/personalCyberLoginPromptModal";
// Form constant for sum insured selection
export const PERSONAL_CYBER_SUM_INSURED_FORM =
  "PERSONAL_CYBER_SUM_INSURED_FORM";

// Sum insured options with raw numeric values for proper formatting
const sumInsuredPlansRaw = [
  {
    id: "cyber_3300",
    value: "3300",
    amount: 3.3,
    features: [
      {
        amount: 189,
        currency: "BHD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 500,
        currency: "USD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5,
        currency: "BHD",
        label: "Deductibles",
        label_ar: "مبلغ التحمل",
      },
    ],
  },
  {
    id: "cyber_4400",
    value: "4400",
    amount: 4.4,
    features: [
      {
        amount: 378,
        currency: "BHD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 1000,
        currency: "USD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5,
        currency: "BHD",
        label: "Deductibles",
        label_ar: "مبلغ التحمل",
      },
    ],
  },
  {
    id: "cyber_6600",
    value: "6600",
    amount: 6.6,
    features: [
      {
        amount: 756,
        currency: "BHD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 2000,
        currency: "USD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5,
        currency: "BHD",
        label: "Deductibles",
        label_ar: "مبلغ التحمل",
      },
    ],
  },
  {
    id: "cyber_8800",
    value: "8800",
    amount: 8.8,
    features: [
      {
        amount: 1323,
        currency: "BHD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 3500,
        currency: "USD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5,
        currency: "BHD",
        label: "Deductibles",
        label_ar: "مبلغ التحمل",
      },
    ],
  },
  {
    id: "cyber_11000",
    value: "11000",
    amount: 11.0,
    features: [
      {
        amount: 1890,
        currency: "BHD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5000,
        currency: "USD",
        label: "Sum-Insured",
        label_ar: "مبلغ التأمين",
      },
      {
        amount: 5,
        currency: "BHD",
        label: "Deductibles",
        label_ar: "مبلغ التحمل",
      },
    ],
  },
];

const SumInsuredSelection = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { handleNextSubstep } = usePersonalCyberFlow();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const partyCode = useSelector(selectPartyCode);
  const reduxPersonalCyberQuote = useSelector(selectPersonalCyberQuote);

  // Get current locale for number formatting
  const currentLocale = i18n.resolvedLanguage === "ar" ? "en-US" : "en-US";

  // Format plans dynamically based on locale - use English numerals for Arabic
  const sumInsuredPlans = useMemo(() => {
    return sumInsuredPlansRaw.map((plan) => ({
      ...plan,
      // Format plan name with proper locale - use English numerals for both
      name: formatPremiumWithCurrency(plan.amount, "BHD", "en-US"),
      name_ar: `${formatPremiumValue(plan.amount, "en-US", 3)} د.ب`, // Reversed order for Arabic
      // Format features with proper locale - use English numerals for Arabic
      features: plan.features.map((feature) => {
        const currencySymbol =
          feature.currency === "USD"
            ? "USD"
            : i18n.resolvedLanguage === "ar"
            ? "د.ب"
            : "BHD";
        const currencySymbol_ar =
          feature.currency === "USD" ? "دولار أمريكي" : "د.ب";

        // Format based on currency type - use English numerals for Arabic
        let formattedAmount_en, formattedAmount_ar;
        if (feature.currency === "USD") {
          // USD uses comma formatting without decimals for whole numbers
          formattedAmount_en = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(feature.amount);
          // Use en-US for Arabic to get English numerals
          formattedAmount_ar = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(feature.amount);
        } else {
          // BHD uses 3 decimal places - use English numerals for Arabic
          formattedAmount_en = formatPremiumValue(feature.amount, "en-US", 3);
          formattedAmount_ar = formatPremiumValue(feature.amount, "en-US", 3); // Use en-US for English numerals
        }

        return {
          en: `${currencySymbol} ${formattedAmount_en} ${feature.label}`,
          ar: `${feature.label_ar} ${formattedAmount_ar} ${currencySymbol_ar}`,
        };
      }),
    }));
  }, [i18n.resolvedLanguage]);

  // State for selected plan
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showBenefitsModal, setShowBenefitsModal] = useState(false);

  // Quote state
  const [quoteData, setQuoteData] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteHasFailed, setQuoteHasFailed] = useState(false);

  // Get all form data to check if we have basic info
  const personalCyberFormData = useSelector(selectPersonalCyberForm);

  // Get profile data for logged-in users
  const profileData = useSelector(selectProfileData);

  // Determine if user is logged in
  const isLoggedIn = Boolean(loginTokenInfo);

  // Form data hook
  const { savePersonalCyberDataInRedux, redux_form_data } =
    usePersonalCyberFormData(PERSONAL_CYBER_SUM_INSURED_FORM, () => {});

  // Load saved selection if available
  useEffect(() => {
    if (redux_form_data && redux_form_data.selectedSumInsured) {
      const savedPlan = sumInsuredPlans.find(
        (plan) => plan.value === redux_form_data.selectedSumInsured
      );
      if (savedPlan) {
        setSelectedPlan(savedPlan);
      }
    }
  }, [redux_form_data, sumInsuredPlans]);

  // Fetch party code for logged-in users
  useEffect(() => {
    if (isLoggedIn && !partyCode && loginTokenInfo?.cpr) {
      console.log('Personal Cyber - Fetching party code for logged-in user:', loginTokenInfo.cpr);
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isLoggedIn, partyCode, loginTokenInfo?.cpr, dispatch]);

  // Fetch profile data if user is logged in but profile data is missing
  useEffect(() => {
    if (isLoggedIn && !profileData) {
      console.log("Fetching profile data for logged-in user");
      dispatch(fetchProfileInfo());
    }
  }, [isLoggedIn, profileData, dispatch]);

  // Initialize quoteData from Redux when user navigates back
  useEffect(() => {
    // If we have a quote in Redux but not in local state, restore it
    if (reduxPersonalCyberQuote && !quoteData) {
      console.log("Restoring quote from Redux:", reduxPersonalCyberQuote);
      setQuoteData(reduxPersonalCyberQuote);
      setQuoteHasFailed(false);
    }
  }, [reduxPersonalCyberQuote]);

  // Handle view benefits modal
  const handleViewBenefits = () => {
    setShowBenefitsModal(true);
  };

  // Multiple login detection methods
  useEffect(() => {
    if (isWaitingForLogin && loginTokenInfo) {
      console.log("Login detected via token change, proceeding to next step");
      setIsWaitingForLogin(false);
      setShowLoginModal(false);

      // Save form data and proceed
      if (selectedPlan) {
        const formData = { selectedSumInsured: selectedPlan.value };
        savePersonalCyberDataInRedux(formData);
        handleNextSubstep();
      }
    }
  }, [
    loginTokenInfo,
    isWaitingForLogin,
    selectedPlan,
    savePersonalCyberDataInRedux,
    handleNextSubstep,
  ]);

  // Additional effect to handle when user logs in from outside and returns to page
  useEffect(() => {
    if (isLoggedIn && showLoginModal && isWaitingForLogin) {
      console.log(
        "Login detected via isLoggedIn change, proceeding to next step"
      );
      setShowLoginModal(false);
      setIsWaitingForLogin(false);

      // Save form data and proceed
      if (selectedPlan) {
        const formData = { selectedSumInsured: selectedPlan.value };
        savePersonalCyberDataInRedux(formData);
        handleNextSubstep();
      }
    }
  }, [
    isLoggedIn,
    showLoginModal,
    isWaitingForLogin,
    selectedPlan,
    savePersonalCyberDataInRedux,
    handleNextSubstep,
  ]);

  // Listen for storage changes to detect login from popup window
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user" || e.key === "loginToken") {
        console.log("Storage change detected, checking for login");
        const tokenInfo = getLoggedInUserDataFromLocalStorage();
        if (tokenInfo) {
          dispatch(setLoginTokenInfo(tokenInfo));
          setIsWaitingForLogin(false);
          setShowLoginModal(false);

          // Save form data and proceed
          if (selectedPlan) {
            const formData = { selectedSumInsured: selectedPlan.value };
            savePersonalCyberDataInRedux(formData);
            handleNextSubstep();
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, selectedPlan, savePersonalCyberDataInRedux, handleNextSubstep]);

  // Polling mechanism as fallback
  useEffect(() => {
    if (!isWaitingForLogin) return;

    const pollForLogin = setInterval(() => {
      const tokenInfo = getLoggedInUserDataFromLocalStorage();
      if (tokenInfo) {
        console.log("Login detected via polling, proceeding to next step");
        dispatch(setLoginTokenInfo(tokenInfo));
        setIsWaitingForLogin(false);
        setShowLoginModal(false);

        // Save form data and proceed
        if (selectedPlan) {
          const formData = { selectedSumInsured: selectedPlan.value };
          savePersonalCyberDataInRedux(formData);
          handleNextSubstep();
        }
        clearInterval(pollForLogin);
      }
    }, 1000);

    return () => clearInterval(pollForLogin);
  }, [
    isWaitingForLogin,
    dispatch,
    selectedPlan,
    savePersonalCyberDataInRedux,
    handleNextSubstep,
  ]);

  // Handle plan selection - automatically fetch quote
  const handlePlanSelect = async (plan) => {
    console.log("=== Plan Selected ===", plan);
    setSelectedPlan(plan);

    // Clear previous quote data and reset states
    setQuoteData(null);
    setQuoteHasFailed(false);

    // Start loading and fetch quote automatically
    await fetchQuoteForPlan(plan);
  };

  // Build a synthetic passenger from the start-page basic info so guests
  // also get a quote on the plan-picker. Returns null if info is incomplete.
  const buildGuestPassengerFromBasicInfo = () => {
    const basicInfo =
      personalCyberFormData?.PERSONAL_CYBER_BASIC_INFO_FORM || {};
    const { fullName, email, contactNumber, birthDate, cpr } = basicInfo;
    if (!fullName || !email || !contactNumber || !birthDate) return null;
    const [year = "", month = "", day = ""] = birthDate.split("-");
    return {
      fullName,
      emailAddress: email,
      mobileNumber: contactNumber,
      cprNumber: cpr || "",
      dateOfBirth: { day, month, year, fullDate: birthDate },
    };
  };

  // Function to fetch quote for a specific plan
  const fetchQuoteForPlan = async (plan) => {
    console.log("=== Fetching Quote for Plan ===", plan);
    console.log("Is logged in:", isLoggedIn);
    console.log("Profile data:", profileData);

    // Guest path: use start-page basic info so guests also get a price.
    if (!isLoggedIn) {
      const guestPassenger = buildGuestPassengerFromBasicInfo();
      if (!guestPassenger) {
        console.log("Quote API call skipped - guest basic info incomplete");
        setQuoteData(null);
        setQuoteHasFailed(false);
        setIsLoadingQuote(false);
        return;
      }
      setIsLoadingQuote(true);
      try {
        const formDataForAPI = {
          [PERSONAL_CYBER_SUM_INSURED_FORM]: { selectedSumInsured: plan.value },
          PERSONAL_CYBER_CONTACT_DETAILS_FORM: { passengers: [guestPassenger] },
        };
        // Dispatch via Redux thunk so the price lands in personalCyberQuote
        // and stays visible on the contact details page (don't refetch there).
        const action = await dispatch(fetchPersonalCyberQuote(formDataForAPI));
        const payload = action?.payload;
        const success = !!(payload?.result?.success && payload?.result?.data);
        if (success) {
          setQuoteData(payload.result.data);
          setQuoteHasFailed(false);
        } else {
          console.error("Personal Cyber guest quote failed:", payload?.result?.error);
          setQuoteHasFailed(true);
          setQuoteData(null);
        }
      } catch (error) {
        console.error("Personal Cyber guest quote exception:", error);
        setQuoteHasFailed(true);
        setQuoteData(null);
      } finally {
        setIsLoadingQuote(false);
      }
      return;
    }

    // Logged-in path requires profile data
    if (!profileData) {
      console.log("Quote API call skipped - profile data not yet available");
      setQuoteData(null);
      setQuoteHasFailed(false);
      setIsLoadingQuote(false);
      return;
    }

    setIsLoadingQuote(true);

    try {
      let formDataForAPI;

      // LOGGED-IN USER: Use profile data only
      console.log("Using profile data for logged-in user");

      // Check if we already have contact details form data
      const existingContactDetails =
        personalCyberFormData?.["PERSONAL_CYBER_CONTACT_DETAILS_FORM"];

      if (existingContactDetails) {
        // Use existing contact details and add the selected plan
        formDataForAPI = {
          ...personalCyberFormData,
          [PERSONAL_CYBER_SUM_INSURED_FORM]: { selectedSumInsured: plan.value },
        };
      } else {
        // Parse the date of birth from profile data
        const parseProfileDateOfBirth = (dateString) => {
          if (!dateString)
            return { day: "", month: "", year: "", fullDate: "" };

          // Try to parse different formats
          let parts = [];
          if (dateString.includes("-")) {
            parts = dateString.split("-"); // YYYY-MM-DD or DD-MM-YYYY
          } else if (dateString.includes("/")) {
            parts = dateString.split("/"); // DD/MM/YYYY or MM/DD/YYYY
          }

          if (parts.length !== 3)
            return { day: "", month: "", year: "", fullDate: dateString };

          // Assume YYYY-MM-DD format from profile
          return {
            day: parts[2],
            month: parts[1],
            year: parts[0],
            fullDate: dateString,
          };
        };

        const profileDateOfBirth = parseProfileDateOfBirth(profileData.DateOfBirth);
        console.log("Profile date of birth parsed:", profileDateOfBirth);

        // Create contact details from profile data
        formDataForAPI = {
          [PERSONAL_CYBER_SUM_INSURED_FORM]: { selectedSumInsured: plan.value },
          PERSONAL_CYBER_CONTACT_DETAILS_FORM: {
            passengers: [
              {
                fullName:
                  profileData.FirstName && profileData.LastName
                    ? `${profileData.FirstName} ${profileData.LastName}`.trim()
                    : profileData.FullName || "User Name",
                emailAddress: profileData.Email || "user@example.com",
                mobileNumber: profileData.PhoneNumber || "12345678",
                cprNumber: profileData.CPR || "123456789",
                nationality: profileData.Nationality || "",
                gender: profileData.Gender === "M" ? "Male" : profileData.Gender === "F" ? "Female" : "Male", // Convert M/F to Male/Female for display
                dateOfBirth: profileDateOfBirth,
              },
            ],
          },
        };
      }

      console.log(
        "Making Personal Cyber quote API call with data:",
        formDataForAPI
      );

      // CRITICAL: For logged-in users, wait for partyCode to be fetched
      // Don't use loginTokenInfo.cpr as fallback - wait for the actual partyCode
      let effectivePartyCode;

      if (isLoggedIn) {
        // Logged-in user - MUST use partyCode from Redux
        if (!partyCode) {
          console.log("⏳ Waiting for party code to be fetched for logged-in user...");
          // Trigger fetch if not already fetching
          if (loginTokenInfo?.cpr) {
            dispatch(fetchPartyCode(loginTokenInfo.cpr));
          }
          setIsLoadingQuote(false);
          toast.error(
            translate(
              "Loading account information. Please try again.",
              "جاري تحميل معلومات الحساب. يرجى المحاولة مرة أخرى."
            )
          );
          return;
        }
        effectivePartyCode = partyCode;
      } else {
        // Guest user - use FALCON
        effectivePartyCode = "FALCON";
      }

      console.log('=== Personal Cyber Quote Party Code Selection ===');
      console.log('Redux partyCode:', partyCode);
      console.log('User CPR:', loginTokenInfo?.cpr);
      console.log('Is logged in:', isLoggedIn);
      console.log('Effective party code:', effectivePartyCode);

      const response = await getPersonalCyberQuote(formDataForAPI, effectivePartyCode);

      if (response.success) {
        setQuoteData(response.data);
        setQuoteHasFailed(false);
        console.log("Personal Cyber quote success:", response.data);
      } else {
        console.error("Personal Cyber quote failed:", response.error);
        setQuoteHasFailed(true);
        setQuoteData(null);

        // Show error toast
        toast.error(
          translate(
            "Unable to calculate premium at the moment. Please try again later.",
            "غير قادر على حساب القسط في الوقت الحالي. يرجى المحاولة مرة أخرى لاحقاً."
          )
        );

        // Don't reset selectedPlan - keep it selected but without quote data
      }
    } catch (error) {
      console.error("Personal Cyber quote exception:", error);
      setQuoteHasFailed(true);
      setQuoteData(null);

      toast.error(
        translate(
          "Unable to calculate premium at the moment. Please try again later.",
          "غير قادر على حساب القسط في الوقت الحالي. يرجى المحاولة مرة أخرى لاحقاً."
        )
      );

      // Don't reset selectedPlan - keep it selected but without quote data
    } finally {
      setIsLoadingQuote(false);
    }
  };

  // Handle continue button
  const handleContinue = async () => {
    console.log("Continue button clicked", selectedPlan);

    // Check if user is logged in
    if (!isLoggedIn) {
      console.log("User not logged in, showing login modal");
      setShowLoginModal(true);
      setIsWaitingForLogin(true);
      return;
    }

    // User is logged in, proceed directly
    console.log("User is logged in, proceeding to next step");
    try {
      const formData = { selectedSumInsured: selectedPlan.value };
      await savePersonalCyberDataInRedux(formData);
      handleNextSubstep();
    } catch (error) {
      console.error("Error saving form data:", error);
    }
  };

  const handleLoginSuccess = () => {
    console.log("handleLoginSuccess called");
    setShowLoginModal(false);
    setIsWaitingForLogin(false);
    if (selectedPlan) {
      const formData = { selectedSumInsured: selectedPlan.value };
      savePersonalCyberDataInRedux(formData);
      handleNextSubstep();
    }
  };

  // Handle Show Summary click
  const handleShowSummary = () => {
    console.log("=== Show Summary clicked ===");
    console.log("Selected plan:", selectedPlan);
    console.log("Quote data:", quoteData);

    // Quote should already be available from plan selection
    setShowSummaryModal(true);
  };

  const handleSkipLogin = () => {
    console.log("handleSkipLogin called");
    setShowLoginModal(false);
    setIsWaitingForLogin(false);
    if (selectedPlan) {
      const formData = { selectedSumInsured: selectedPlan.value };
      savePersonalCyberDataInRedux(formData);
      handleNextSubstep();
    }
  };

  return (
    <>
      <PersonalCyberLoginPromptModal
        show={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setIsWaitingForLogin(false);
        }}
        onLoginSuccess={handleLoginSuccess}
        onSkip={handleSkipLogin}
      />

      <PersonalCyberJourneyLayout is_hide_back_btn={false}>
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_label={translate("Continue", "التالي")}
          btn_disabled={!selectedPlan || isLoadingQuote}
          show_summary={
            !!selectedPlan && !isLoadingQuote && !quoteHasFailed && !!quoteData
          }
          price={quoteData?.totalPremium || 0}
          onShowSummary={handleShowSummary}
          isLoading={isLoadingQuote}
          addonComponent={null}
        >
          <div className="flex flex-col items-center p-6 max767:p-0 mx-auto pt-0 py-5 w-full">
            <div className="max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0 w-full max-w-4xl">
              {/* Header */}
              <div className="text-center mb-8">
                <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left max767:rtl:text-right">
                  {translate("Required Information", "المعلومات المطلوبة")}
                </h1>
                <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left max767:rtl:text-right">
                  {translate(
                    "Please select a sum insured limit from the list below.",
                    "يرجى اختيار مبلغ التأمين من القائمة أدناه."
                  )}
                </p>
              </div>

              {/* Sum Insured Selection Cards - Flex layout with centered alignment */}
              <div className="flex flex-wrap justify-center gap-3 mb-8 max767:flex-col max767:items-center ipad:gap-4 w-full max-w-[900px] mx-auto">
                {sumInsuredPlans.map((plan, index) => {
                  const isSelected = selectedPlan?.id === plan.id;
                  const isDisabled = isLoadingQuote && !isSelected;
                  const isRTL = i18n.resolvedLanguage === "ar";

                  return (
                    <div key={`wrapper-${plan.id}`} className="contents">
                      <div
                        className={`flex relative w-[280px] min-w-[280px] h-auto flex-col p-5 pb-4 rounded-[10px] border-solid box-border max767:w-full max767:min-w-full transition-all duration-300 ${
                          isDisabled
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg"
                        } ${
                          isSelected
                            ? "border-2 border-[#00719A] bg-[#E3F8FF]"
                            : "border border-[#DDE2E4] bg-white"
                        }`}
                        onClick={() =>
                          !isLoadingQuote && handlePlanSelect(plan)
                        }
                      >
                        {/* Radio button indicator */}
                        <div className="absolute top-4 right-4 rtl:right-auto rtl:left-4">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              isSelected
                                ? "border-[#00A9E7] bg-[#00A9E7]"
                                : "border-[#DDE2E4] bg-[#F6F8F9]"
                            }`}
                          >
                            {isSelected ? (
                              <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                            ) : null}
                          </div>
                        </div>

                        {/* Plan price as header */}
                        <div className="w-full">
                          <h3 className="text-[#1A2024] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] text-left rtl:text-right rtl:flex-row-reverse mb-3">
                            {translate(plan.name, plan.name_ar)}
                          </h3>
                        </div>

                        {/* Features with checkmarks */}
                        <div className="flex flex-col gap-1 w-full mb-1">
                          {plan.features.map((feature, featureIndex) => (
                            <div
                              key={featureIndex}
                              className="feature flex gap-2 items-start"
                            >
                              <img
                                src={tickicon}
                                alt="tick icon"
                                className="mt-0.5 flex-shrink-0 w-3 h-3"
                              />
                              <p className="text-black text-xs not-italic font-normal leading-[150%] text-left">
                                {translate(feature.en, feature.ar)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* View Benefits Button */}
              <div className="flex justify-center mb-6">
                <button
                  className="flex items-center gap-2 border bg-white px-4 py-2 rounded-[10px] border-solid border-[#DDE2E4] text-[#1A2024] text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
                  onClick={handleViewBenefits}
                >
                  <img src={listblackicon} alt="list icon" />
                  {translate(
                    "View full list of benefits & covers",
                    "عرض القائمة الكاملة للمزايا والتغطيات"
                  )}
                </button>
              </div>
            </div>
          </div>
        </BottomStickyBtnLayout>
      </PersonalCyberJourneyLayout>

      {/* Travel Summary Modal (Reused) */}
      <TravelSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        selectedPlan={{
          ...selectedPlan,
          // Add quote data properties that TravelSummaryModal expects
          premium: quoteData?.premium || 0,
          tax: quoteData?.tax || 0,
          totalPremium: quoteData?.totalPremium || 0,
          quoteNumber: quoteData?.quoteNumber || quoteData?.policyNumber,
          apiData: quoteData,
          isRealApiData: true,
        }}
      />

      {/* Personal Cyber Benefits Modal */}
      <PersonalCyberBenefitsModal
        show={showBenefitsModal}
        onClose={() => setShowBenefitsModal(false)}
        plans={sumInsuredPlans}
      />
    </>
  );
};

export default SumInsuredSelection;
